import { useEffect, useState } from "react";
import { Loader2, Download, Archive, ArchiveRestore, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { ADMIN_KINDS, StatusBadge, fmtDate, fmtValue, downloadBlob } from "./adminConfig";

const H = ({ children }) => <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{children}</h3>;

const Details = ({ cfg, r }) => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
    {cfg.fields.map(([k, l, wide]) => (
      <div key={k} className={wide ? "col-span-2" : ""}>
        <dt className="text-xs text-slate-500">{l}</dt>
        <dd className={`mt-1 text-sm text-[#0B1220] ${wide ? "whitespace-pre-wrap rounded-md bg-[#F4F6F8] p-3 leading-relaxed" : "break-words"}`} data-testid={`detail-${k}`}>
          {k === "profile_url" && r[k] ? <a href={r[k]} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-[#2563EB] underline">{r[k]} <ExternalLink className="h-3 w-3" /></a> : fmtValue(r[k])}
        </dd>
      </div>
    ))}
  </dl>
);

const StatusEditor = ({ cfg, r, onSave }) => {
  const [status, setStatus] = useState(r.status);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); await onSave(`status`, { status, note: note || null }); setNote(""); setBusy(false); };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-4">
      <label htmlFor="status-select" className="text-sm font-medium text-[#0B1220]">Change status</label>
      <select id="status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm" data-testid="drawer-status-select">
        {cfg.statuses.map((s) => <option key={s}>{s}</option>)}
      </select>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note about this change" aria-label="Status change note" className="w-full rounded-md border border-slate-300 p-3 text-sm" data-testid="drawer-status-note" />
      <button type="button" disabled={busy || status === r.status} onClick={save} className="btn-primary h-10 px-4 text-sm" data-testid="drawer-status-save-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update status
      </button>
    </div>
  );
};

const Notes = ({ r, onSave }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => { if (!text.trim()) return; setBusy(true); await onSave("notes", { text }); setText(""); setBusy(false); };
  return (
    <div>
      <H>Internal notes ({r.notes.length})</H>
      <ul className="space-y-3" data-testid="drawer-notes-list">
        {r.notes.map((n) => (
          <li key={n.id} className="rounded-md border border-slate-200 bg-[#F4F6F8] p-3 text-sm">
            <p className="whitespace-pre-wrap text-[#0B1220]">{n.text}</p>
            <p className="mt-2 text-xs text-slate-500">{n.by} · {fmtDate(n.at)}</p>
          </li>
        ))}
      </ul>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add an internal note (not visible to the submitter)" aria-label="New note" className="mt-3 w-full rounded-md border border-slate-300 p-3 text-sm" data-testid="drawer-note-input" />
      <button type="button" disabled={busy || !text.trim()} onClick={add} className="btn-secondary mt-2 h-10 px-4 text-sm" data-testid="drawer-note-add-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Add note</button>
    </div>
  );
};

const History = ({ r }) => (
  <div>
    <H>Status and activity history</H>
    <ol className="space-y-3 border-l border-slate-200 pl-4" data-testid="drawer-history-list">
      {[...r.history].reverse().map((h, i) => (
        <li key={i} className="text-sm">
          <p className="text-[#0B1220]">{h.action === "status_changed" ? <>Status: {h.from} → <strong>{h.to}</strong></> : h.action === "created" ? <>Created as <strong>{h.to}</strong></> : h.action.replace("_", " ")}</p>
          <p className="text-xs text-slate-500">{h.by} · {fmtDate(h.at)}</p>
        </li>
      ))}
    </ol>
  </div>
);

const NOTIFY_LABEL = { sent: "Sent", failed: "Failed", not_configured: "Not configured" };
const Notifications = ({ r }) => (
  <div>
    <H>Email notifications</H>
    {r.notifications.length === 0 ? <p className="text-sm text-slate-500">No notifications were attempted.</p> : (
      <ul className="space-y-2 text-sm" data-testid="drawer-notifications-list">
        {r.notifications.map((n, i) => (
          <li key={i} className="flex justify-between gap-4"><span className="text-slate-600">{n.type === "admin" ? "Admin alert" : "Submitter confirmation"} → {n.to}</span><span className={n.status === "sent" ? "text-emerald-700" : "text-amber-700"}>{NOTIFY_LABEL[n.status] || n.status}{n.error ? ` (${n.error})` : ""}</span></li>
        ))}
      </ul>
    )}
  </div>
);

export const RecordDrawer = ({ kind, id, onClose, onChanged, canManage }) => {
  const cfg = ADMIN_KINDS[kind];
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!id) { setR(null); return; }
    setError("");
    api.get(`/admin/records/${kind}/${id}`).then(({ data }) => setR(data)).catch((e) => setError(formatApiError(e)));
  }, [kind, id]);

  const mutate = async (action, body) => {
    try {
      const method = action === "notes" ? "post" : "patch";
      const { data } = await api[method](`/admin/records/${kind}/${id}/${action}`, body);
      setR(data);
      onChanged();
      toast.success(action === "notes" ? "Note added" : action === "archive" ? (body.archived ? "Record archived" : "Record restored") : "Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadCv = async () => {
    try {
      const res = await api.get(`/admin/records/${kind}/${id}/cv`, { responseType: "blob" });
      downloadBlob(res.data, r.cv.filename);
    } catch {
      toast.error("CV download failed");
    }
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-2xl" data-testid="record-drawer">
        <div className="border-b border-slate-200 bg-[#F4F6F8] px-6 py-6">
          <SheetDescription className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{cfg.singular}</SheetDescription>
          <SheetTitle className="mt-2 font-display text-xl font-semibold text-[#0B1220]" data-testid="drawer-reference">{r?.reference || "Loading…"}</SheetTitle>
          {r && <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500"><StatusBadge status={r.status} /> Received {fmtDate(r.created_at)} · Updated {fmtDate(r.updated_at)} {r.archived && <span className="font-semibold text-amber-700">· Archived</span>}</div>}
        </div>
        {error && <p className="p-6 text-sm text-red-600">{error}</p>}
        {!r && !error && <div className="p-10"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>}
        {r && (
          <div className="space-y-8 px-6 py-6">
            <StatusEditor key={r.status} cfg={cfg} r={r} onSave={mutate} />
            <div><H>Submission details</H><Details cfg={cfg} r={r} /></div>
            {kind === "applications" && (
              <div><H>CV / professional profile</H>
                {r.cv ? <button type="button" onClick={downloadCv} className="btn-secondary h-10 px-4 text-sm" data-testid="drawer-cv-download-button"><Download className="h-4 w-4" /> {r.cv.filename}</button> : <p className="text-sm text-slate-500">No file uploaded.</p>}
              </div>
            )}
            <Notes r={r} onSave={mutate} />
            <History r={r} />
            <Notifications r={r} />
            {canManage && <div className="border-t border-slate-200 pt-6">
              <button type="button" onClick={() => mutate("archive", { archived: !r.archived })} className="btn-secondary h-10 px-4 text-sm" data-testid="drawer-archive-button">
                {r.archived ? <><ArchiveRestore className="h-4 w-4" /> Restore record</> : <><Archive className="h-4 w-4" /> Archive record</>}
              </button>
              <p className="mt-2 text-xs text-slate-500">Archived records are permanently deleted when the retention policy is applied after the configured retention period.</p>
            </div>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
